import {
  isEqual,
  isMap,
  isObject,
  isObjectLike,
  mapKeys,
  size,
  uniq,
} from 'lodash';
import {
  DeepPartial,
  Entries,
  ItemOrItemId,
  Merge,
  NotArray,
  PartialRecord,
  PrimitiveType,
  ValidIdItem,
  TypeMap,
  TypeFilter,
  TypeFilterMap,
  OptionalReadonly,
  ValidRecordKeys,
} from '../models/type-helpers.model';
import { guards } from './typeguards.helpers';
import { isOneOf } from './array.helpers';
import { compare } from './compare.helpers';

/**
 * Empty object that will not cause rerenders.
 */
export const EMPTY_FROZEN_OBJECT = Object.freeze({}) as unknown as Record<
  string,
  unknown
>;
/**
 * Create a new object including a subset of propetries from an object
 * @param obj
 * @param keys
 * @returns
 */
export function pick<T extends object, K extends keyof T>(
  obj: T,
  ...keys: K[]
): Pick<T, K> {
  return filterObject(obj, (v, key) => isOneOf(keys, key)) as Pick<T, K>;
}

/**
 * Create a new object including a subset of propetries from an object
 * @param obj
 * @param predicate Function to filter properties. Return truthy to keep, falsy to remove
 * @returns
 */
export function filterObject<T extends object, K extends keyof T>(
  obj: T,
  predicate: (value: T[K], key: K) => boolean | undefined,
): { [P in keyof T]?: T[P] } {
  if (!isObject(obj)) {
    throw new Error('Not an object');
  }

  let hasRemovedKey = false;
  const result: Partial<T> = {};

  // Heavily used function, so use for loop instead of reduce (AI optimized this)
  for (const [rawKey, value] of Object.entries(obj)) {
    const key = rawKey as K;
    if (predicate(value as T[K], key)) {
      result[key] = value as T[K];
      continue;
    }
    hasRemovedKey = true;
  }

  // Return original object if no keys were removed to keep referential equality
  return hasRemovedKey ? result : obj;
}

/**
 * Clone an object and excluding a set of properties from the original
 * @param obj
 * @param keys
 * @returns
 */
export function omit<T extends object, K extends keyof Merge<NonNullable<T>>>(
  obj: T = {} as T,
  ...keys: (K | undefined)[]
): Omit<T, K> {
  // Filter out undefined keys and keys that doesn't exist in the object
  const definedKeys = keys.filter(isDefined).filter((k) => k in obj);

  // Only create new object if we have keys to remove
  if (definedKeys.length) {
    return filterObject(obj, (v, key) => !isOneOf(definedKeys, key)) as Omit<
      T,
      K
    >;
  }
  // If no keys are provided, return the original object
  return obj;
}

/**
 * Remove properties with a specific value from an object.
 * If no keys are removed, return the original object.
 * @param obj
 * @param values Values to exclude
 * @returns
 */
export const omitValues = <T extends object>(
  obj: T,
  ...values: unknown[]
): Partial<T> => {
  if (!isNonArrayObject(obj)) {
    throw new Error('Not an object');
  }
  return filterObject(obj, (value) => !isOneOf(values, value));
};

/**
 * Remove any undefined/null values from an object.
 * If no keys are removed, return the original object.
 * @param obj
 * @returns
 */
export const omitUndefined = <T extends object>(obj: T): Partial<T> =>
  omitValues(obj, null, undefined);

/**
 * Get new object only containing properties not false, null, 0, undefined or empty string
 * @param obj
 * @returns
 */
export function omitFalsy<T extends object>(obj: T): Partial<T> {
  if (typeof obj !== 'object') {
    throw new Error('Not an object');
  }
  return filterObject(obj, (value) => Boolean(value));
}

/**
 * Check if properties exist on object
 * @param obj
 * @param keys
 * @returns
 */
export function hasProperties<T extends object, K extends keyof T>(
  obj: T | undefined,
  ...keys: K[]
): obj is T & Pick<T, K> {
  if (!isObject(obj)) {
    return false;
  }
  return keys.every((key) => key in obj);
}

/**
 * Check if properties exist on object and is defined (not undefined or null)
 * @param obj
 * @param keys
 * @returns
 */
export function hasDefinedProperties<T extends object, K extends keyof T>(
  obj: Partial<T> | undefined,
  ...keys: K[]
): obj is T & Required<Pick<T, K>> {
  return (
    hasProperties(obj, ...keys) && keys.every((key) => isDefined(obj[key]))
  );
}

/**
 * Check if one or more properties exist on object and is defined (not undefined or null)
 * @param obj
 * @param keys
 * @returns
 */
export function hasSomeDefinedProperties<T extends object, K extends keyof T>(
  obj: Partial<T> | undefined,
  ...keys: K[]
): obj is T & Required<Pick<T, K>> {
  if (!isObject(obj)) {
    return false;
  }
  return Object.keys(obj)
    .filter((key) => keys.includes(key as K))
    .some((key) => isDefined(obj[key as K]));
}

/**
 * Check if properties exist on object and is defined
 * @param obj
 * @param keys
 * @returns
 */
export function getMissingProperties<T extends object, K extends keyof T>(
  obj: Partial<T> | undefined,
  ...keys: K[]
): K[] {
  return keys.filter((key) => !isDefined(obj?.[key]));
}

/**
 * Check if properties exist on object and is truty (not false, null, 0, undefined or empty string)
 * @param obj
 * @param keys
 * @returns
 */
export function hasTruthyProperties<T extends object, K extends keyof T>(
  obj: Partial<T>,
  ...keys: K[]
): obj is T & Required<Pick<T, K>> {
  return hasProperties(obj, ...keys) && keys.every((key) => !!obj[key]);
}

/**
 * Detect if subset of an object is the same as the original property
 * @param element
 * @param changes
 * @param keys If keys are provided, only those keys will be checked for changes
 * @returns
 */
export const isPartialEqual = <T extends object>(
  element: T,
  changes: Partial<T>,
  ...keys: (keyof T)[]
): boolean => {
  // If the element is the same as the changes, don't waste resources checking for changes
  if (element === changes) {
    return true;
  }

  // Only check for changes in existing keys
  keys = keys.length ? keys.filter((key) => key in changes) : getKeys(changes);

  return isEqual(pick(changes, ...keys), pick(element, ...keys));
};

const isDefined = <T>(value: T | undefined | null): value is T =>
  value !== undefined && value !== null;

/**
 * Typesafe version of Object.keys.
 * Will sort keys alphabetically as default to simplify comparison
 * @param obj
 * @param sort Sort keys alphabetically (easier to compare)
 * @returns
 */
export const getKeys = <T extends object>(
  obj: T | null | undefined,
  sort = true,
): Array<keyof T> => {
  const keys = isDefined(obj) ? (Object.keys(obj) as Array<keyof T>) : [];
  return sort ? keys.sort() : keys;
};

/**
 * Get unique keys from an array of objects
 * @param array
 * @returns
 */
export const getKeysInObjectArray = <T extends object>(
  ...array: T[]
): (keyof T)[] => uniq(array.flatMap((obj) => getKeys(obj)));

/**
 * Typesafe version of Object.entries
 * @param obj
 * @returns
 */
export const getEntries = <T extends object>(obj: T): Entries<T> =>
  Object.entries(obj) as Entries<T>;

/**
 * Convert an enum to a record
 * @param enumObj
 * @returns
 */
export const enumToRecord = <T extends object>(
  enumObj: T,
): Record<keyof T, T[keyof T]> => {
  if (typeof enumObj !== 'object' || !enumObj) {
    throw new Error('Not an enum');
  }
  return getKeys(enumObj).reduce(
    (acc, key) => {
      // Remove the numeric keys from the enum
      if (typeof key === 'string' && !isFinite(+key)) {
        acc[key] = enumObj[key];
      }
      return acc;
    },
    {} as Record<keyof T, T[keyof T]>,
  );
};

/**
 * Map a record to a new record and allow filtering of items.
 * @param record Record to map/filter
 * @param valueMap Function to map values, return undefined to filter out
 * @returns
 */
export const mapFilterRecord = <
  T extends Record<K, T[K]> | Partial<Record<K, T[K]>>,
  K extends keyof T,
  R,
>(
  record: T,
  valueMap: (item: T[K], key: K) => R | undefined,
): Record<K, R> => {
  const clone = {} as Record<K, R>;

  getKeys(record).forEach((key) => {
    const value = valueMap(record[key] as T[K], key as K);

    if (value !== undefined) {
      clone[key as K] = value;
    }
  });
  return clone;
};

/**
 * Similar to Object.assign but will apply changes deeply. Note: Can not be used to delete properties since it's only merging in the changes
 * TODO: Rename to mergeChanges or deepObjectAssign?
 * @param original
 * @param changes A subset of the original object to change. Try to keep as small as possible
 * @returns
 */
export const applyChanges = <T>(original: T, changes: DeepPartial<T>): T => {
  if (changes === original) {
    return original;
  }

  // Update primitives & functions right away
  if (isPrimitive(changes) || typeof changes === 'function') {
    return changes as T;
  }
  // Overwrite completely if type changed
  if (
    Array.isArray(original) !== Array.isArray(changes) ||
    typeof original !== typeof changes
  ) {
    return changes as T;
  } else if (Array.isArray(changes)) {
    const array = Array.isArray(original) ? (original as unknown[]) : [];

    const updated = changes.map((change, i) =>
      applyChanges(array[i], change as DeepPartial<T>),
    );

    return (compare.equal.shallow(array, updated) ? array : updated) as T;
  } else if (typeof changes === 'object') {
    const keys = getKeys(changes);

    // Only update if needed to not trigger to many rerenders of views
    if (changes && keys.length > 0) {
      keys.forEach((key) => {
        const change = changes[key] as DeepPartial<T[keyof T]>;
        const current = isObject(original) ? original[key] : undefined;
        const updated = applyChanges(current, change);

        // Equals, no need to update
        if (current !== updated) {
          original = { ...original, [key]: updated };
        }
      });
    }
  }
  return original;
};

/**
 * Replace properties in an object with new values if anything has changed.
 * Will use isEqual to detect changes so new arrays/objects will not cause changes unless their content has changed
 * @param obj Original object to replace properties in
 * @param changes Changes to apply to the object
 * @param ignoreUndefined If true, undefined values will not replace existing values
 * @returns New object with the updated properties or the original object if no changes were made
 */
export const replaceProperties = <T extends object>(
  obj: T,
  changes?: Partial<T>,
  ignoreUndefined = false,
): T => {
  if (!changes) return obj;
  const keys = ignoreUndefined
    ? getKeys(changes).filter((key) => changes[key])
    : getKeys(changes);
  return keys.reduce((result, key) => {
    const value = changes[key];
    return isEqual(obj[key], value) ? result : { ...result, [key]: value };
  }, obj);
};

/*
 * Throw an error if changes are made to specific keys in an object.
 * @param obj Original object that should be modified
 * @param changes Changes to the object
 * @param forbiddenKeys Keys that are not allowed to have changes
 * @returns
 */
export const preventChangesInKeys = <T extends object>(
  obj: T,
  changes: Partial<T>,
  ...forbiddenKeys: (keyof T)[]
): T => {
  if (!isPartialEqual(obj, changes, ...forbiddenKeys)) {
    throw new Error(
      'Changes are not allowed for properties: ' +
        forbiddenKeys.map((key) => `"${key.toString()}"`).join(', '),
    );
  }
  return obj;
};

/**
 * @deprecated: Use guards.is.isPrimitive instead
 * @param value
 * @returns
 */
export const isPrimitive = (value: unknown): value is PrimitiveType => {
  const type = typeof value;
  return !value || (type !== 'object' && type !== 'function');
};

export const getId = <T extends ValidIdItem>(
  itemOrId: ItemOrItemId<T>,
): T['id'] => (isPrimitive(itemOrId) ? itemOrId : itemOrId.id);

export const getName = <T extends { name?: string }>(
  itemOrName: T | T['name'],
): T['name'] => (isPrimitive(itemOrName) ? itemOrName : itemOrName.name);

/**
 * Lists keys whose values differ between two plain objects (lodash deep compare).
 * @param a - First object
 * @param b - Second object
 * @returns Keys present on either object where values are not equal
 */
export const compareObjectsWithAllKeys = <A extends object, B extends object>(
  a: A,
  b: B,
): string[] =>
  Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).filter(
    (key) => !isEqual(Reflect.get(a, key), Reflect.get(b, key)),
  );

export const getNumber = <
  T extends PartialRecord<K, T[K] | undefined>,
  K extends keyof T,
>(
  obj: T,
  key: K,
  defaultValue = 0,
): number => {
  const value = obj[key];
  return typeof value === 'number' && isFinite(value) ? value : defaultValue;
};

/**
 * @de: Use guards.is.isEmptyObject instead
 * @param obj
 * @returns
 */
export const isEmptyObject = (obj: unknown): boolean =>
  isNonArrayObject(obj) && Object.keys(obj).length === 0;

/**
 * @de: Use guards.is.object instead
 * @param obj
 * @returns
 */
export const isNonArrayObject = (obj: unknown): obj is NotArray<object> =>
  isObjectLike(obj) && !Array.isArray(obj);

/**
 * Get common keys from multiple objects.
 * Will ignore properties that are undefined
 * @param objects
 * @returns
 */
export const getCommonKeys = <T extends object>(
  ...objects: T[]
): (keyof T)[] => {
  if (objects.length === 0) {
    return [];
  }

  return objects.reduce((acc, obj) => {
    return getKeys(obj).filter(
      (key) => isDefined(obj[key]) && acc.includes(key),
    );
  }, getKeys(objects[0]));
};

/**
 * Get all keys from multiple objects
 * @param objects
 * @returns
 */
export const getAllKeys = <T extends object>(...objects: T[]): (keyof T)[] =>
  uniq(objects.flatMap((obj) => getKeys(omitUndefined(obj))));

/**
 * A more accurate type of function than typeof.
 * Introduces new types for null and array (typeof null/array is object)
 * @param value
 * @returns
 */
export const getTypeOf = (value: unknown): keyof TypeMap => {
  const type = typeof value;

  if (type === 'object') {
    if (value === null) {
      return 'null';
    }
    if (Array.isArray(value)) {
      return 'array';
    }
    return 'object';
  }
  return type;
};

/**
 * Check if value is of a specific type
 * @param value
 * @param expectedType
 * @returns
 */
export const isType = <T extends keyof TypeMap, V>(
  value: V | TypeMap<V>[T],
  ...expectedTypes: T[]
): value is TypeMap<V>[T] => isOneOf(expectedTypes, getTypeOf(value));

/**
 * Todo Fix typing to return correct type only (not original type)
 * @param obj
 * @param type
 * @returns
 */
export const filterPropertiesByType = <
  O extends object,
  T extends keyof TypeFilterMap<O>,
>(
  obj: O,
  type: T,
): TypeFilter<O, T> => {
  return mapFilterRecord(obj, (value) =>
    isType(value as TypeFilterMap<O>[keyof TypeFilterMap<O>], type)
      ? value
      : undefined,
  ) as TypeFilter<O, T>;
};

/**
 * Remap keys of an object
 * @param obj
 * @param keyMap
 * @returns
 */
export const mapRecordKeys = <
  T extends Record<string, unknown>,
  K extends keyof T,
>(
  obj: T,
  keyMap: Record<K, string> | Map<K, string>,
): T => {
  const recordMap = isMap(keyMap) ? Object.fromEntries(keyMap) : keyMap;
  return mapKeys(obj, (value, key) => recordMap[key] ?? key) as T;
};

export const mapObject = <T extends object, K extends keyof T, R>(
  obj: T,
  map: (value: T[K], key: K) => R,
): Record<K, R> => {
  if (!size(obj)) {
    return obj as Record<K, R>;
  }
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, map(value, key as K)]),
  ) as Record<K, R>;
};

/**
 * Test if the element has any changes
 * @param element
 * @param changes
 * @param keys If keys are provided, only those keys will be checked for changes
 * @returns
 */
export const hasPartialChanges = <T extends object>(
  element: T,
  changes: Partial<T>,
  ...keys: (keyof T)[]
): boolean =>
  element !== changes && // Don't check properties if the element is the same as the changes
  !isPartialEqual(element, keys.length ? pick(changes, ...keys) : changes);

/**
 * Get the depth of an object/array
 * @param obj
 * @param current
 * @returns
 */
export const getObjectDepth = (obj: unknown, current = 0): number => {
  if (typeof obj !== 'object' || obj === null) return current;
  return Math.max(
    ...Object.values(obj).map((val) => getObjectDepth(val, current + 1)),
  );
};

/**
 * Create a record with all keys set to zero
 * @param keys
 * @returns
 */
export const createZeroRecord = <K extends ValidRecordKeys>(
  keys: OptionalReadonly<K[]>,
): Record<K, number> => {
  const result = {} as Record<K, number>;
  keys.forEach((key) => {
    result[key] = 0;
  });
  return result;
};

/**
 * Recursively sort object keys to guarantee deterministic enumeration order.
 * Non-object values pass through unchanged; array element order is preserved
 * but nested objects within arrays are also sorted.
 * Typically paired with {@link getObjectHash} for deterministic hashing.
 */
export function sortObject<T>(value: T): T {
  if (guards.is.array(value)) return value.map(sortObject) as T;
  if (!guards.is.object(value)) return value;
  return Object.keys(value)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      const child = value[key];
      acc[key] = Array.isArray(child)
        ? child.map(sortObject)
        : sortObject(child);
      return acc;
    }, {}) as T;
}

/**
 * Generate a 32-bit hash string from an object (xxHash-style; not interoperable with xxHash).
 * Useful for creating fingerprints to detect data changes.
 * Hash depends on property enumeration order; equal values in different key order yield different hashes.
 * Input is serialized with {@link JSON.stringify}: undefined in arrays becomes null, non-enumerable
 * properties are omitted, and custom toJSON affects the result.
 *
 * @param obj - The object to hash
 * @returns A hexadecimal hash string (8 characters)
 */
export const getObjectHash = (() => {
  const PRIME32_1 = 0x9e3779b1;
  const PRIME32_2 = 0x85ebca77;
  const PRIME32_3 = 0xc2b2ae3d;
  const PRIME32_4 = 0x27d4eb2f;
  const PRIME32_5 = 0x165667b1;

  const rotl32 = (x: number, r: number): number => (x << r) | (x >>> (32 - r));

  return function nodonHash32(obj: object): string {
    const str = JSON.stringify(obj);
    let h32 = (PRIME32_5 + str.length) >>> 0;

    for (let i = 0; i < str.length; i++) {
      h32 = Math.imul(h32 + Math.imul(str.charCodeAt(i), PRIME32_5), PRIME32_1);
      h32 = rotl32(h32, 17);
      h32 = Math.imul(h32, PRIME32_4);
    }

    // final avalanche
    h32 ^= h32 >>> 15;
    h32 = Math.imul(h32, PRIME32_2);
    h32 ^= h32 >>> 13;
    h32 = Math.imul(h32, PRIME32_3);
    h32 ^= h32 >>> 16;
    return (h32 >>> 0).toString(16).padStart(8, '0');
  };
})();

/**
 * Incrementally tracks the serialized size of objects using {@link JSON.stringify}.
 * Caches per-object sizes in a WeakMap so each object is only serialized once.
 */
export const createSizeTracker = <T extends object>() => {
  const cache = new WeakMap<T, number>();
  let total = 0;

  const estimate = (item: T): number => {
    const cached = cache.get(item);
    if (cached !== undefined) return cached;
    const size = JSON.stringify(item).length;
    cache.set(item, size);
    return size;
  };

  return {
    get total() {
      return total;
    },
    track: (item: T): number => (total += estimate(item)),
    untrack: (item: T): number => {
      total -= cache.get(item) ?? 0;
      cache.delete(item);
      return total;
    },
    reset: () => {
      total = 0;
    },
  };
};
